import AccountContext from 'context/AccountContext';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import { useContext } from 'react';
import { getParkzoneState } from 'stores/parkzoneStore';
import { usePermitStore } from 'stores/permitStore';
import {
  ConfirmCreateSalesOrderDataProps,
  PermitDataInputMeta,
} from 'types/common';
import {
  BillingFrequency,
  BillingType,
  Card,
  CreateSalesOrderInput,
  Customer,
  Pmc,
  SalesOrderType,
  Vehicle,
} from 'types/generatedSchemaTypes';
import { isSalesOrderFree } from 'utils/commonUtils';
import Logger from 'utils/Logger';

type FactoryInput = {
  customer: Customer;
  pmcId?: string;
  startDate: Date | undefined;
  selectedVehicle?: Vehicle;
  selectedPaymentMethod?: Card;
  selectedPmc?: Pmc;
  parkingSpaceUid?: string;
  parkingSpaceName?: string;
  billingType?: BillingType;
  billingFrequency?: BillingFrequency;
  unlockCode?: string;
  type?: SalesOrderType;
  automaticOnlineOrderActivation: boolean;
};

const createSalesOrderInput = ({
  customer,
  pmcId,
  startDate = new Date(),
  selectedVehicle,
  selectedPaymentMethod,
  selectedPmc,
  parkingSpaceUid,
  parkingSpaceName,
  billingType = 'ANNIVERSARY',
  billingFrequency = 'MONTHLY',
  unlockCode,
  type,
  automaticOnlineOrderActivation,
}: FactoryInput): [CreateSalesOrderInput, PermitDataInputMeta] | undefined => {
  const { name, prices, parkZone, salesLocation, countryCode } =
    selectedPmc || {};
  const isFreeSalesOrder = isSalesOrderFree(prices);

  const validity = {
    validFrom: startDate,
    ...(selectedPmc?.endSellingDate
      ? { validTo: new Date(selectedPmc.endSellingDate) }
      : {}),
  };

  const orderLines = [
    {
      pmcId: pmcId ?? '',
      quantity: 1,
      validity,
      billingFrequency,
      billingType,
      parkingSpaceUids: parkingSpaceUid ? [parkingSpaceUid] : [],
      registrationPlates: selectedVehicle
        ? [
            {
              countryCode: selectedVehicle.licensePlate.countryCode,
              plateText: selectedVehicle.licensePlate.plateText,
            },
          ]
        : [],
      unlockCode,
    },
  ];

  const permitInput: CreateSalesOrderInput = {
    customerNumber: customer.customerNumber as string,
    countryCode: countryCode || 'FI',
    paymentTerm: 30,
    orderLines,
    aggregateInvoice: true,
    orderStatus: 'DRAFT',
    serviceChannelRef: process.env.SERVICE_CHANNEL_CODE as string,
    paymentMethod: 'CREDIT_CARD',
    creditCardId: isFreeSalesOrder ? '' : (selectedPaymentMethod?.cardId ?? ''),
    unlockCode,
    type,
  };

  const meta: PermitDataInputMeta = {
    name,
    prices,
    parkZone,
    salesLocation,
    vehicle: selectedVehicle,
    paymentMethod: selectedPaymentMethod,
    validity: permitInput.orderLines?.find(Boolean)?.validity,
    billingType,
    parkingSpaceName,
    freeSalesOrder: isFreeSalesOrder,
    billingFrequency,
    automaticOnlineOrderActivation,
  };
  return [permitInput, meta];
};

const useCreateSalesOrderInputFactory = () => {
  const selectedVehicle = useSelectedParkingVehicle();
  const selectedPaymentMethod = useSelectedPaymentMethod();
  const { customer } = useContext(AccountContext);

  const getParkingSpaceDetails = () => {
    const { parkingSpaceUid, name } =
      getParkzoneState().selectedParkingSpace ?? {};
    return { parkingSpaceUid, parkingSpaceName: name };
  };

  const createInput = (
    permitData: ConfirmCreateSalesOrderDataProps,
    type: SalesOrderType
  ) => {
    if (!permitData) {
      return null;
    }

    const { startDate, pmcId, selectedPmc, unlockCode } = permitData ?? {};
    const { parkingSpaceUid, parkingSpaceName } = getParkingSpaceDetails();
    const { selectedBillingFrequency: billingFrequency } =
      usePermitStore.getState().selectedPermitBillingInterval ?? {};

    return createSalesOrderInput({
      customer,
      pmcId,
      startDate,
      selectedVehicle,
      selectedPaymentMethod,
      selectedPmc,
      parkingSpaceUid,
      parkingSpaceName,
      billingType: permitData?.meta?.billingType,
      billingFrequency,
      unlockCode,
      type,
      automaticOnlineOrderActivation:
        permitData?.meta?.automaticOnlineOrderActivation ?? false,
    });
  };
  return { createInput };
};
export default useCreateSalesOrderInputFactory;
